import { useEffect, useState } from 'react'
import config from '@/config'
import {
  Plus,
  Trash2,
  Edit,
  Globe,
  GlobeLock,
  Pin,
  Loader2,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import '@/styles/quill-dark.css'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
import announcementsService from '@/services/announcementsService'
import countryHubService from '@/services/countryHubService'
import ImageUploader from '@/components/ImageUploader'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useConfirmacion } from '@/hooks/useConfirmacion'

const CATEGORIES = [
  { value: 'announcement', label: 'Anuncio' },
  { value: 'news', label: 'Noticia' },
  { value: 'communication', label: 'Comunicado' },
  { value: 'achievement', label: 'Logro' },
]

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link'],
    ['clean'],
  ],
}

function emptyForm() {
  return {
    _id: null,
    title: '',
    excerpt: '',
    content: '',
    category: 'announcement',
    coverImageUrl: '',
    isPinned: false,
    metaTitle: '',
    metaDescription: '',
  }
}

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('es', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : ''

export default function CountryAnnouncements() {
  const { confirmar, dialogo } = useConfirmacion()
  const { partner } = useAuthStore()
  const countryCode = partner?.managedCountry || ''
  const publicUrl = `${config.portalUrl.replace(/\/+$/, '')}/${countryCode.toLowerCase()}`

  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [form, setForm] = useState(emptyForm())

  useEffect(() => {
    load()
  }, [])

  async function load() {
    try {
      setIsLoading(true)
      const res = await announcementsService.list()
      setItems(res.data || [])
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al cargar anuncios')
    } finally {
      setIsLoading(false)
    }
  }

  function openNew() {
    setForm(emptyForm())
    setEditorOpen(true)
  }

  function openEdit(item) {
    setForm({
      _id: item._id,
      title: item.title || '',
      excerpt: item.excerpt || '',
      content: item.content || '',
      category: item.category || 'announcement',
      coverImageUrl: item.coverImageUrl || '',
      isPinned: !!item.isPinned,
      metaTitle: item.metaTitle || '',
      metaDescription: item.metaDescription || '',
    })
    setEditorOpen(true)
  }

  async function save() {
    if (!form.title?.trim()) {
      toast.error('El título es obligatorio')
      return
    }
    try {
      setIsSaving(true)
      if (form._id) {
        await announcementsService.update(form._id, form)
        toast.success('Anuncio actualizado')
      } else {
        await announcementsService.create(form)
        toast.success('Anuncio creado en borrador')
      }
      setEditorOpen(false)
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error al guardar')
    } finally {
      setIsSaving(false)
    }
  }

  async function togglePublish(item) {
    const next = item.status !== 'published'
    try {
      await announcementsService.publish(item._id, next)
      toast.success(next ? 'Publicado' : 'Despublicado')
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error')
    }
  }

  async function remove(item) {
    const ok = await confirmar({
      titulo: 'Archivar anuncio',
      descripcion: `¿Archivar «${item.title}»? Deja de verse en tu sitio.`,
      textoConfirmar: 'Archivar',
      variante: 'destructiva',
    })
    if (!ok) return
    try {
      await announcementsService.remove(item._id)
      toast.success('Archivado')
      await load()
    } catch (e) {
      toast.error(e.response?.data?.message || 'Error')
    }
  }

  if (partner?.organizationType !== 'country_manager') {
    return (
      <PageContainer>
        <Alert variant="warning">
          <AlertTriangle size={16} />
          <AlertTitle>Solo Country Managers</AlertTitle>
          <AlertDescription>
            Solo los Country Managers pueden gestionar anuncios.
          </AlertDescription>
        </Alert>
      </PageContainer>
    )
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-12">
          <Loader2 size={28} className="animate-spin text-primary" />
        </div>
      </PageContainer>
    )
  }

  const statusBadge = (status) => {
    if (status === 'published')
      return <Badge variant="success">Publicado</Badge>
    if (status === 'archived') return <Badge variant="secondary">Archivado</Badge>
    return <Badge variant="default">Borrador</Badge>
  }

  return (
    <PageContainer>
      <PageHeader
        title="Anuncios y noticias"
        description={
          <>
            Blog corporativo de tu país. Publicaciones aparecen en{' '}
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-medium hover:underline inline-flex items-center gap-1 break-all"
            >
              {publicUrl} <ExternalLink size={12} />
            </a>
          </>
        }
        actions={
          <Button onClick={openNew}>
            <Plus size={16} /> Nuevo anuncio
          </Button>
        }
      />

      {items.length === 0 ? (
        <Card>
          <CardContent className="text-center py-10">
            <h3 className="font-display text-h2 font-semibold text-foreground mb-1">
              Aún no tienes anuncios
            </h3>
            <p className="text-body-sm text-subtle mb-4">
              Crea tu primer anuncio para mantener informada a tu comunidad legal.
            </p>
            <Button onClick={openNew}>
              <Plus size={16} /> Crear anuncio
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <Card key={item._id}>
              <CardContent className="pt-5 pb-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap gap-1.5 mb-1.5">
                      {item.isPinned && (
                        <Badge variant="warning" className="gap-1">
                          <Pin size={11} /> Destacado
                        </Badge>
                      )}
                      <Badge variant="outline">
                        {CATEGORIES.find((c) => c.value === item.category)?.label ||
                          item.category}
                      </Badge>
                      {statusBadge(item.status)}
                      {item.publishedAt && (
                        <span className="text-caption text-subtle">
                          {formatDate(item.publishedAt)}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-h3 font-semibold text-foreground">
                      {item.title}
                    </h3>
                    {item.excerpt && (
                      <p className="text-body-sm text-subtle mt-1">{item.excerpt}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => togglePublish(item)}
                      aria-label={
                        item.status === 'published' ? 'Despublicar' : 'Publicar'
                      }
                      className={
                        item.status === 'published' ? 'text-warning' : 'text-success'
                      }
                    >
                      {item.status === 'published' ? (
                        <GlobeLock size={16} />
                      ) : (
                        <Globe size={16} />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(item)}
                      aria-label="Editar"
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(item)}
                      disabled={item.status === 'archived'}
                      aria-label="Archivar"
                      className="text-danger hover:bg-danger-50 dark:hover:bg-danger-500/15"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{form._id ? 'Editar anuncio' : 'Nuevo anuncio'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ann-title">Título</Label>
              <Input
                id="ann-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ann-excerpt">Resumen (excerpt)</Label>
              <Textarea
                id="ann-excerpt"
                rows={2}
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              />
              <p className="text-caption text-subtle">
                Aparece en la lista de noticias.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Contenido</Label>
              <div className="rounded-md border border-line overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={form.content}
                  onChange={(v) => setForm({ ...form, content: v })}
                  modules={QUILL_MODULES}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Categoría</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Destacar (pinned al top)</Label>
                <div className="flex items-center gap-2 h-12">
                  <Switch
                    checked={form.isPinned}
                    onCheckedChange={(v) => setForm({ ...form, isPinned: !!v })}
                  />
                  <span className="text-body-sm text-foreground">
                    {form.isPinned ? 'Destacado' : 'Normal'}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Imagen de portada</Label>
              <ImageUploader
                type="gallery"
                currentUrl={form.coverImageUrl}
                height={180}
                onUploadRequest={async (file) => {
                  const res = await countryHubService.uploadImage(file, 'gallery')
                  return res.data
                }}
                onUpload={(data) => setForm({ ...form, coverImageUrl: data.url })}
                onDelete={() => setForm({ ...form, coverImageUrl: '' })}
                enableUnsplash
                unsplashCategory="documents_signing"
              />
              <Input
                placeholder="O URL externa"
                value={form.coverImageUrl}
                onChange={(e) => setForm({ ...form, coverImageUrl: e.target.value })}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ann-meta-title">Meta title (SEO)</Label>
                <Input
                  id="ann-meta-title"
                  value={form.metaTitle}
                  onChange={(e) => setForm({ ...form, metaTitle: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ann-meta-desc">Meta description (SEO)</Label>
                <Input
                  id="ann-meta-desc"
                  value={form.metaDescription}
                  onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={save} disabled={isSaving}>
              {isSaving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {dialogo}
    </PageContainer>
  )
}
